// Helpers for narrowing and ordering a crew list: the "filter by function"
// UI on the read-only schedule timeline and the schedule creator's
// "Drag to assign" pick list. Producers want to see only one-take shooters,
// only aftermovie shooters, etc., without losing the per-crew visibility
// toggles that already exist alongside.
//
// Pure functions so the inclusion/exclusion and ordering contracts are
// unit-testable independent of any render.

import { toggleInSet } from "@/lib/toggleInSet";

// Only the function is required — these helpers are generic over the
// caller's own crew type and return T, never this shape.
export type CrewWithFunction = {
  function: string;
};

// One shared comparator so every list of role labels — the filter chips and
// the sorted crew list — orders the same strings the same way.
const collator = new Intl.Collator(undefined, { sensitivity: "base" });
const byText = (a: string, b: string) => collator.compare(a, b);

// Set of the available role labels in alphabetical order — drives the
// checklist UI. Empty input → empty list (no filter renders).
export function listCrewFunctions(crew: CrewWithFunction[]): string[] {
  const seen = new Set<string>();
  for (const c of crew) {
    if (c.function && c.function.trim() !== "") seen.add(c.function);
  }
  return Array.from(seen).sort(byText);
}

// True when this crew member should be visible given the producer's
// selected function set. An empty selection means "no filter applied"
// — every crew member passes. A non-empty selection narrows to
// crew whose function is in the set.
export function crewPassesFunctionFilter(
  crewFunction: string,
  selected: ReadonlySet<string>,
): boolean {
  if (selected.size === 0) return true;
  return selected.has(crewFunction);
}

// Convenience filter for whole crew lists. Preserves input order.
export function applyFunctionFilter<T extends CrewWithFunction>(
  crew: T[],
  selected: ReadonlySet<string>,
): T[] {
  if (selected.size === 0) return crew;
  return crew.filter((c) => selected.has(c.function));
}

// Toggle a function in/out of the selection. Thin alias over the shared
// toggleInSet so stage and function filters can't drift apart.
export function toggleFunction(selected: ReadonlySet<string>, fn: string): Set<string> {
  return toggleInSet(selected, fn);
}

export type CrewSortOrder = "crew" | "name" | "function";

/** Labels for the sort control, kept next to the type so it can't go stale. */
export const CREW_SORT_LABELS: Record<CrewSortOrder, string> = {
  crew: "Crew order",
  name: "Name (A–Z)",
  function: "Function (A–Z)",
};

const byName = (a: { name?: string | null }, b: { name?: string | null }) =>
  byText(a.name ?? "", b.name ?? "");

/**
 * Order a crew list for display. "crew" keeps the producer's own ordering
 * (the order crew were added, which carries meaning), "name" and "function"
 * sort alphabetically — function falls back to name so members sharing a
 * role keep a stable, predictable order rather than an arbitrary one.
 * Always returns a new array; the input is never mutated.
 */
export function sortCrew<T extends CrewWithFunction & { name?: string | null }>(
  crew: T[],
  order: CrewSortOrder,
): T[] {
  if (order === "crew") return [...crew];
  if (order === "name") return [...crew].sort(byName);
  return [...crew].sort((a, b) => byText(a.function, b.function) || byName(a, b));
}
